import React from 'react';
import { Box, Typography, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Unauthorized from "../../../layouts/Unauthorized";

// Public privacy policy served at /privacy (ezsplit.io/privacy). Referenced by the
// iOS App Store submission (App Privacy → Privacy Policy URL) and the app stores.
// Keep in sync with ezsplit/ios/docs/privacy-policy.md.

const LAST_UPDATED = 'July 5, 2026';
const SUPPORT_EMAIL = 'support@ezsplit.io';

const collected = [
    ['Name', 'Shown to the people you split bills with'],
    ['Email address', 'Account identity, sign-in, and account-related email (confirmation, password reset)'],
    ['Password', 'Authenticates you; stored only as a salted hash by our identity provider (AWS Cognito) — we never see it in plaintext'],
    ['Two-factor (TOTP) enrollment', 'Required multi-factor authentication to protect your account'],
    ['Venmo username (optional)', 'So other participants can pay you back; shown only to members of bills you share'],
    ['Bills, expenses, groups, split rules, and settlement records you create', 'The core function of the app — tracking who owes what'],
];

const Section = ({ title, children }) => (
    <Box component="section" sx={{ mb: 4 }}>
        {title ? <Typography variant="h5" component="h2" sx={{ mb: 1.5, fontWeight: 600 }}>{title}</Typography> : null}
        {children}
    </Box>
);

const P = ({ children }) => (
    <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7 }}>{children}</Typography>
);

const ViewPrivacyPolicy = () => {
    return (
        <Unauthorized>
            <Box sx={{ maxWidth: 820, mx: 'auto', px: 3, py: { xs: 4, md: 6 } }}>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    EZSplit Privacy Policy
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
                    Last updated: {LAST_UPDATED}
                </Typography>

                <P>
                    EZSplit (“we”, “us”) helps you split bills and settle up with the people you
                    share expenses with. This policy explains what we collect, why, and the choices
                    you have. Questions:{' '}
                    <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
                </P>

                <Section title="Information we collect">
                    <P>
                        We only collect what the app needs to work. We do <strong>not</strong> sell
                        your data, and we do <strong>not</strong> use third-party advertising or
                        analytics/tracking SDKs.
                    </P>
                    <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Why we collect it</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {collected.map(([data, why]) => (
                                <TableRow key={data}>
                                    <TableCell sx={{ verticalAlign: 'top' }}>{data}</TableCell>
                                    <TableCell>{why}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <P>
                        We do <strong>not</strong> link bank accounts and do <strong>not</strong>{' '}
                        collect bank credentials or account/routing numbers.
                    </P>
                </Section>

                <Section title="How we use your information">
                    <P>
                        We use your information to provide bill-splitting (creating bills, computing
                        splits, and tracking payments between participants), to authenticate you and
                        secure your account (including mandatory two-factor authentication), and to
                        send account-related messages (email confirmation, password reset).
                    </P>
                </Section>

                <Section title="How your information is shared">
                    <P>
                        <strong>With people you share bills with.</strong> When you add someone to a
                        bill, they can see your name, the amounts involved, and — if you linked one —
                        your Venmo username, so they can pay you back.
                    </P>
                    <P>
                        <strong>With service providers who run the app for us.</strong> We use Amazon
                        Web Services (AWS) for authentication (Cognito), database, and hosting. They
                        process data on our behalf under their own security and privacy commitments.
                    </P>
                    <P>We do <strong>not</strong> sell your personal information or share it for advertising.</P>
                </Section>

                <Section title="Data retention and deletion">
                    <P>
                        You can permanently delete your account at any time from <strong>Profile →
                        Delete my account</strong> inside the app. Deleting your account removes your
                        sign-in identity and deletes/anonymizes your associated records. Some records
                        that other participants rely on (for example, a shared bill’s history) may be
                        retained in de-identified form. You may also email{' '}
                        <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> to request deletion.
                    </P>
                </Section>

                <Section title="Security">
                    <P>
                        Traffic between the app and our servers is encrypted with HTTPS/TLS. Accounts
                        are protected by mandatory time-based one-time-password (TOTP) two-factor
                        authentication. No system is perfectly secure, but we take reasonable measures
                        to protect your data.
                    </P>
                </Section>

                <Section title="Children’s privacy">
                    <P>
                        EZSplit is not directed to children under 13, and we do not knowingly collect
                        information from them.
                    </P>
                </Section>

                <Section title="Changes to this policy">
                    <P>
                        We may update this policy from time to time. Material changes will be
                        reflected by the “Last updated” date above.
                    </P>
                </Section>

                <Section title="Contact">
                    <P>
                        Questions or requests:{' '}
                        <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
                    </P>
                </Section>
            </Box>
        </Unauthorized>
    );
};

export default ViewPrivacyPolicy;
